/**
 * Labstep
 *
 * @module containers/Entity/Read/Entities
 * @desc Container for reading entities
 */

import type { EntityName } from 'labstep-web/services/schema/schema';
import {
  readEntitiesNextPage,
  readEntitiesPage,
} from 'labstep-web/state/actions/entity';
import {
  selectReadPageEntities,
  selectReadPageEntitiesAllPages,
  selectReadPageStatus,
  selectReadPageStatusAllPages,
  selectReadPageTotal,
  selectReadTotalPages,
} from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  EntityReadPageContainerChildrenProps,
  IEntityReadPageContainerProps,
} from './types';

export const EntityReadPageContainer = <
  T extends EntityName = EntityName,
>({
  children,
  entityName,
  params,
  page,
  usePostFilter,
  injectFields,
}: IEntityReadPageContainerProps<T>): React.ReactElement | null => {
  const dispatch = useDispatch();

  // Use selectors to get state
  const entities = useSelector((state) =>
    selectReadPageEntities(state, entityName, params, page),
  );
  const entitiesAllPages = useSelector((state) =>
    selectReadPageEntitiesAllPages(state, entityName, params),
  );
  const status = useSelector((state) =>
    selectReadPageStatus(state, entityName, params, page),
  );
  const statusAllPages = useSelector((state) =>
    selectReadPageStatusAllPages(state, entityName, params),
  );
  const totalPages = useSelector((state) =>
    selectReadTotalPages(state, entityName, params),
  );
  const total = useSelector((state) =>
    selectReadPageTotal(state, entityName, params),
  );

  // Memoized read functions
  const read = useCallback(
    (options?: any) => {
      dispatch(
        readEntitiesPage(
          entityName,
          params,
          page,
          options,
          usePostFilter,
          injectFields,
        ),
      );
    },
    [dispatch, entityName, params, page, usePostFilter, injectFields],
  );

  const readNextPage = useCallback(
    (options?: any) => {
      dispatch(
        readEntitiesNextPage(
          entityName,
          params,
          options,
          usePostFilter,
          injectFields,
        ),
      );
    },
    [dispatch, entityName, params, page, usePostFilter, injectFields],
  );
  return children({
    entities,
    entitiesAllPages,
    status,
    statusAllPages,
    totalPages,
    total,
    read,
    readNextPage,
  });
};
